// src/components/NotificationProvider.jsx
import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  registerDeviceToken, 
  setupForegroundNotifications,
  getNotificationNavigationPath 
} from '../firebase/firebase';
import { 
  fetchUnreadCount 
} from '../features/notifications/notificationSlice';
import './NotificationToast.css';

const NotificationProvider = ({ children }) => {
  const dispatch = useDispatch();
  const { isAuthenticated } = useSelector((state) => state.auth);
  
  const [notification, setNotification] = useState(null);
  const [showToast, setShowToast] = useState(false);

  // ❌ TEST CODE REMOVED

  const handleNotificationReceived = useCallback((payload) => {
    setNotification({
      title: payload.title || payload.notification?.title || 'New Notification',
      body: payload.body || payload.notification?.body || '',
      data: payload.data || {}
    });
    setShowToast(true);
    playNotificationSound();
  }, []);

  // Auto close after 10 seconds
  useEffect(() => {
    if (showToast) {
      const timer = setTimeout(() => {
        setShowToast(false);
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [showToast]);

  // Initialize notifications when authenticated
  useEffect(() => {
    if (!isAuthenticated) return;

    let unsubscribe = () => {};

    const initializeNotifications = async () => {
      try {
        await registerDeviceToken();
        unsubscribe = setupForegroundNotifications(handleNotificationReceived);
        dispatch(fetchUnreadCount());
      } catch (error) {
        // Failed to initialize notifications
      }
    };

    initializeNotifications();

    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, [isAuthenticated, dispatch, handleNotificationReceived]);

  // Periodically refresh unread count
  useEffect(() => {
    if (!isAuthenticated) return;

    const interval = setInterval(() => {
      dispatch(fetchUnreadCount());
    }, 60000);

    return () => clearInterval(interval);
  }, [isAuthenticated, dispatch]);

  // Handle notification click
  const handleToastClick = () => {
    if (notification?.data) {
      const path = getNotificationNavigationPath({ Data: notification.data });
      const basePath = process.env.NODE_ENV === 'production' ? '/provider' : '';
      window.location.href = `${basePath}${path}`;
    }
    setShowToast(false);
  };

  const getNotificationIcon = (type) => {
    const typeUpper = (type || '').toUpperCase();
    switch (typeUpper) {
      case 'NEW_BOOKING_REQUEST':
      case 'NEW_BOOKING':
        return '📦';
      case 'DRIVER_ON_THE_WAY':
        return '🚗';
      case 'DRIVER_ARRIVED':
        return '📍';
      case 'SERVICE_STARTED':
        return '🔧';
      case 'JOB_COMPLETED':
        return '✅';
      case 'CUSTOMER_RATING':
      case 'NEW_REVIEW':
        return '⭐';
      case 'BOOKING_CANCELLED':
        return '❌';
      case 'DRIVER_SIGNUP_COMPLETE':
      case 'DRIVER_INVITED':
        return '👤';
      case 'PAYOUT':
      case 'PAYOUT_RELEASED':
        return '💰';
      default:
        return '🔔';
    }
  };

  return (
    <>
      {children}
      
      {showToast && notification && (
        <div className="notification-toast-container" onClick={handleToastClick}>
          <div className="notification-toast">
            <div className="notification-toast-icon">
              {getNotificationIcon(notification.data?.type)}
            </div>
            <div className="notification-toast-content">
              <div className="notification-toast-title">{notification.title}</div>
              <div className="notification-toast-body">{notification.body}</div>
            </div>
            <div className="notification-toast-progress"></div>
          </div>
        </div>
      )}
    </>
  );
};

// Play notification sound
const playNotificationSound = () => {
  try {
    const audio = new Audio('https://notificationsounds.com/storage/sounds/file-sounds-1150-pristine.mp3');
    audio.volume = 0.5;
    audio.play().catch(() => {});
  } catch (error) {}
};

export default NotificationProvider;